// Mirrors CreateTcrCampaignRequest::SCREENSHOT_MAX_KILOBYTES; the two move together.
export const SCREENSHOT_MAX_BYTES = 1024 * 1024;
export const SCREENSHOT_ACCEPT = 'image/png,image/jpeg,image/webp';
const PASS_THROUGH_TYPES = ['image/png', 'image/jpeg'];
const MAX_PASSES = 4;

const loadImage = (file: File): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            URL.revokeObjectURL(url);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('The screenshot could not be read as an image.'));
        };
        image.src = url;
    });

const toPng = (image: HTMLImageElement, scale: number): Promise<Blob> =>
    new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);
        // PNG only: lossless, and the one raster format TCR accepts that never blurs text.
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('The screenshot could not be encoded.'))),
            'image/png'
        );
    });

export const prepareOptInScreenshot = async (file: File): Promise<File> => {
    if (PASS_THROUGH_TYPES.includes(file.type) && file.size <= SCREENSHOT_MAX_BYTES) {
        return file;
    }

    const image = await loadImage(file);
    const name = `${file.name.replace(/\.[^.]+$/, '')}.png`;
    let scale = 1;

    for (let pass = 0; pass < MAX_PASSES; pass += 1) {
        const blob = await toPng(image, scale);

        if (blob.size <= SCREENSHOT_MAX_BYTES) {
            return new File([blob], name, { type: 'image/png' });
        }

        // PNG size tracks pixel count; shrink proportionally to the overshoot, never below half per pass.
        scale *= Math.max(0.5, Math.sqrt(SCREENSHOT_MAX_BYTES / blob.size));
    }

    throw new Error('The screenshot is too large even after downscaling; crop it to the opt-in form.');
};
